<template>
	<div :class="['integration-card', 'is-' + elemState.class]">
		<div class="integration-card__container">
			<div
				class="integration-card__status"
				:title="elemState.title + ' Site BOD'"
			>
				<i :class="['icon', 'icon-' + elemState.icon]"></i>
			</div>
			<div class="integration-card__main">
				<span class="integration-card__name">
					{{ source.dealerNumberId }}
				</span>
				<span class="integration-card__integration">
					{{ customerName }}
				</span>
				<span class="integration-card__integration">
					CompanyId :
					{{ companyName }}
				</span>
			</div>
		</div>
		<ActionsSet
			class="integration-card__actions"
			:id="`${uid}_action`"
			:variant="variant"
			:options="actions"
		/>
	</div>
</template>

<script>
import ActionsSet from '@/site-components/actions-set/actions-set';

// mixins
import ValidatePermissions from '@/sections/integration/validate-permission-mixins';

//constants
import {
	MODULE_NAME as moduleName,
	SET_INTEGRATION,
	SHOW_MODAL_INTEGRATION,
	MODULE_NAME
} from '@/sections/integration/integrations-mutation-types';
import { mapActions } from 'vuex';
// libraries
import { formatDate } from '@/utils/datetime';
import { sub } from 'date-fns';

export default {
	name: 'IntegrationCard',
	inheritAttrs: false,
	mixins: [ValidatePermissions],
	props: {
		thirdPartyId: {
			type: Number,
			required: true
		},
		source: {
			type: Object
		}
	},
	components: {
		ActionsSet
	},
	computed: {
		customers() {
			return this.$store.getters['options/OptionsCustomers'] || {};
		},
		companyName() {
			return (this.source.companyId || '').toString().padStart(2, '0');
		},
		customerName() {
			const { items } = this.customers;

			const customer = (items || []).find(
				c => c.value == this.source.dealershipGroupGuid
			);

			return (customer && customer.text) || '';
		},
		elemState() {
			if (!this.source.active) {
				return {
					class: 'inactive',
					icon: 'check-indeterminate',
					title: 'Inactive'
				};
			} else {
				return {
					class: 'active',
					icon: 'checkbox',
					title: 'Active'
				};
			}
		},
		actions() {
			return [
				{
					id: 1,
					action: this.edit,
					title: 'Select to go to integration settings',
					iconClass: 'icon icon-manage',
					label: 'Settings',
					type: 'anchor',
					visible: true,
					disabled: !this.canUpdate
				},
				{
					id: 2,
					action: this.copyIntegration,
					title: 'Select to create a copy of this integration',
					iconClass: 'icon icon-copy',
					label: 'Copy Of',
					type: 'anchor',
					disabled: false,
					visible: true
				},
				{
					id: 3,
					action: this.goToSiteBODsPage,
					title: 'Select to go to site bod view',
					iconClass: 'icon icon-company',
					label: 'SiteBODs',
					type: 'anchor',
					visible: true
				},
				{
					id: 4,
					action: '#',
					iconClass: 'icon icon-history',
					label: 'History',
					title: 'Select to go to integration history',
					type: 'anchor',
					visible: true,
					disabled: true
				},

				{
					id: 5,
					action: this.showTransactions,
					iconClass: 'icon icon-transaction',
					label: 'Transactions',
					title: 'Select to go to the transactions page',
					type: 'anchor',
					visible: true
				}
			];
		}
	},
	methods: {
		...mapActions(MODULE_NAME, ['SetReferenceId']),
		edit() {
			if (!this.canUpdate) {
				this.$router.push('/access-denied');
				return;
			}

			this.$store.dispatch(`${moduleName}/${SET_INTEGRATION}`, {
				...this.source
			});
		},
		goToSiteBODsPage() {
			this.$store.commit('integrations/setThirdParty', {
				...this.source
			});
			this.$router.push({
				path: `/integrations/thirdparties/${this.thirdPartyId}/integrations/${this.source.id}/sitebods`
			});
		},
		showTransactions() {
			this.$router.push({
				path: `/integrations/thirdparties/${this.source.partyId}/${this.thirdPartyId}/${this.source.id}/transactions`,

				query: {
					dealershipGroupGuid: this.source.dealershipGroupGuid,
					companyId: this.source.companyId,
					minDatetime: formatDate(
						new Date(sub(new Date(), { weeks: 1 }))
					),
					maxDatetime: formatDate(new Date())
				}
			});
		},
		copyIntegration() {
			this.SetReferenceId(this.source.id);
			this.$store.dispatch(
				`${MODULE_NAME}/${SHOW_MODAL_INTEGRATION}`,
				true
			);
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * Results Card
	* Can use SCSS variables = TRUE
	* [Table of Contents]
    *
    * BASE
    * BASE DESKTOP
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
@import '@/scss/5-objects/_cards.scss';

.integration-card {
	@include card;
	display: grid;
	@include mq($until: md) {
		grid-template-rows: auto;
		row-gap: $gutter-lg;
	}

	&__container {
		display: flex;
	}
	&__main {
		font-weight: $medium-font-weight;
		font-size: $default-font-size;
	}

	&.is-inactive {
		.integration-card__status {
			@include text-contrast($neutral-3, $white, $black);
			background: $neutral-3;
		}
	}

	&__status {
		color: $white;
		margin-right: $gutter-lg;
		@include create-circle(16px, 16px, $ui-positive);
		@include vertical-horizontal-center-with-flex;
	}

	&__name,
	&__integration {
		display: block;
	}

	&__integration {
		font-size: $default-font-size - 2;
		font-weight: $default-font-weight;
	}
}

/*------------------------------------*\
  #BASE TABLET-UP
\*------------------------------------*/
@include mq($from: md) {
	.integration-card {
		column-gap: $gutter-lg;
		grid-template-columns: auto 1fr;
	}
}
</style>
